<template>
  <validation-provider
    :immediate="immediate"
    :rules="rules"
    :name="name"
    :vid="name"
    v-slot="validation"
    tag="div"
  >
    <div
      :class="{
        [$style.disabled]: disabled,
        [$style.invalid]: validation.valid === false,
        [$style.boxed]: boxed,
        [$style.fat]: fat
      }"
    >
      <div :class="$style.label" v-if="label">{{ label }}</div>
      <div :class="$style.grid" :style="gridStyles">
        <label
          v-for="option in options"
          :class="{
            [$style.optionContainer]: true,
            [$style.hideButton]: hideButton,
            [$style.withIcon]: Boolean(option.icon),
            [$style.checked]: option.value === innerValue
          }"
          :key="option.value"
          :for="`radio-${option.value}`"
          ><input
            type="radio"
            :id="`radio-${option.value}`"
            :value="option.value"
            :class="$style.hiddenButton"
            :disabled="disabled"
            v-model="innerValue"
            :name="name"
          />
          <radio-button
            :checked="option.value === innerValue"
            :class="$style.optionButton"
            v-if="!hideButton"
          />
          <div :class="$style.optionLabel">
            <base-icon
              :class="$style.icon"
              v-if="option.icon"
              :name="option.icon"
              themeSize="x-large"
            /><span :class="{ [$style.bold]: bold }">{{ option.label }}</span>
            <div :class="$style.priceContainer" v-if="option.price">
              <div :class="$style.total" v-if="option.total">
                {{ option.total | money(2) }}
              </div>
              <div :class="$style.originalPrice" v-if="option.originalPrice">
                {{ option.originalPrice | money(2) }}
              </div>
              <div :class="$style.price">{{ option.price | money(2) }}/mo</div>
            </div>
          </div>
          <div
            :class="{ [$style.details]: true, [$style.hideButton]: hideButton }"
            v-if="option.apiRequests"
          >
            <div :class="$style.detailsRow">
              <div :class="$style.detailsLabel">HTTP API<br />Requests</div>
              <div :class="$style.detailsValue">{{ option.apiRequests }}</div>
            </div>
            <div :class="$style.detailsRow">
              <div :class="$style.detailsLabel">
                WebSocket:<br />New Blocks/Transactions
              </div>
              <div :class="$style.detailsValue">
                {{ option.wsBlocksTransactions }}
              </div>
            </div>
            <div :class="$style.detailsRow">
              <div :class="$style.detailsLabel">
                WebSocket:<br />Address Monitoring
              </div>
              <div :class="$style.detailsValue">
                {{ option.wsAddressMonitoring }}
              </div>
            </div>
          </div></label
        >
      </div>
      <div :class="$style.error">{{ validation.errors[0] }}</div>
    </div>
  </validation-provider>
</template>

<script>
import { ValidationProvider } from 'vee-validate'

import RadioButton from './RadioButton'

export default {
  name: 'base-radio-group',

  components: { ValidationProvider, RadioButton },

  props: {
    value: { String },
    icon: { String },
    total: { String },
    price: { String },
    bold: { Boolean, default: false },
    hideButton: { Boolean, default: false },
    immediate: { Boolean },
    boxed: { Boolean, default: false },
    fat: { Boolean, default: false },
    disabled: { Boolean, default: false },
    name: { String, required: true },
    label: { String },
    rules: { String },
    options: { Array, default: () => [] },
    colWidth: { String, default: '150px' }
  },

  data: () => ({
    innerValue: null
  }),

  watch: {
    innerValue(val) {
      this.$emit('input', val)
    },

    value(newVal) {
      // required to handle value updates from outside
      this.innerValue = newVal
    }
  },

  computed: {
    gridStyles() {
      return {
        gridTemplateColumns: `repeat(auto-fill, minmax(${this.colWidth}, 1fr))`
      }
    }
  },

  created() {
    // required to prefill with initial value
    this.innerValue = this.value
  }
}
</script>

<style lang="scss" module>
.label {
  color: $color-copy;
  font-size: $size-small;
  font-weight: $weight-regular;
  line-height: 1.5;
  margin-top: spacing(2);
  margin-bottom: spacing(1);
}

.grid {
  display: grid;
  grid-column-gap: spacing(2);
  grid-row-gap: spacing(2);

  .disabled & {
    opacity: 0.6;
  }
}

.optionContainer {
  position: relative;
  padding-left: spacing(4);
  cursor: pointer;

  .disabled & {
    background-color: #efefef;
    cursor: not-allowed;
  }

  .boxed & {
    box-shadow: 0 0 0 1px $color-borders-light;
    border-radius: $radius-large;
    padding: spacing(2) spacing(2) spacing(2) spacing(6);
    transition: box-shadow 0.2s;

    &.checked {
      box-shadow: $shadow-small, 0 0 0 2px $color-primary-light,
        0 3px 4px 0 rgba(0, 143, 202, 0.25);
    }

    &.withIcon {
      padding: spacing(2) spacing(2) spacing(2) spacing(10);
    }

    &.hideButton {
      padding-left: spacing(2);
    }
  }

  .boxed.invalid &.checked {
    box-shadow: $shadow-small, 0 0 0 2px $color-danger,
      0 3px 4px 0 rgba(0, 143, 202, 0.25);
  }

  .fat & {
    padding: spacing(3.5) spacing(2) spacing(3.5) spacing(6);
  }
}

.hiddenButton {
  opacity: 0;
  margin: 0;
  width: 0;
  height: 0;
  display: block;
}

.optionButton {
  position: absolute;
  left: 0;

  .boxed & {
    left: spacing(2);
  }

  :focus + & {
    border-radius: 50%;
    box-shadow: 0 0 6px 1px $color-primary;
  }
}

.optionLabel {
  color: $color-copy;
  font-size: $size-medium;
  font-weight: $weight-regular;
  line-height: 1.25;

  .fat & {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
  }
}

.icon {
  position: absolute;
  left: spacing(6);
  margin-top: -2px;
  margin-right: spacing(1);
}

.bold {
  font-size: $size-medium;
  font-weight: $weight-medium;
  letter-spacing: 0.25px;
  color: $color-copy;
  line-height: 1;
  text-transform: capitalize;
}

.priceContainer {
  position: relative;
}

.price {
  font-size: $size-medium;
  font-weight: $weight-medium;
  line-height: 1.25;
  letter-spacing: 0.15px;
  color: $color-copy;
}

.total,
.originalPrice {
  font-size: $size-small;
  line-height: 1.43;
  color: $color-copy-light;
  position: absolute;
  margin-top: -22px;
  right: 0;
}

.originalPrice {
  text-decoration: line-through;
}

.details {
  border-top: 1px solid $color-borders-light;
  margin: spacing(2) spacing(-2) spacing(-3.5) spacing(-6);
  padding: spacing(1) spacing(2);

  &.hideButton {
    margin-left: spacing(-2);
  }
}

.detailsRow {
  display: flex;
  justify-content: space-between;
  padding: spacing(2) 0;

  & + & {
    border-top: 1px solid $color-borders-light;
  }
}

.detailsLabel {
  font-size: $size-xx-small;
  font-weight: $weight-medium;
  line-height: 1.33;
  letter-spacing: 0.4px;
  color: $color-copy;
}

.detailsValue {
  font-size: $size-xx-small;
  font-weight: $weight-medium;
  line-height: 1.33;
  letter-spacing: 0.4px;
  text-align: right;
  color: $color-primary-light;
  white-space: nowrap;
}

.error {
  font-size: $size-xx-small;
  font-weight: $weight-medium;
  line-height: $size-medium;
  letter-spacing: 0.4px;
  color: $color-danger;
  min-height: spacing(2);
  margin-left: 0;
  margin-top: spacing(1);
  text-align: left;
}
</style>
